import type { Logger as DatadogLogger } from '@datadog/browser-logs';
import type { DatadogLogContext, LoggerConfig } from './logger.util';
import { logLevelHandler, logToDatadog, prependTraceId } from './logger.util';
import { generateTraceId } from './trace-id';

export const createLogMethods = (
  loggerInstance: Nullable<DatadogLogger>,
  config: LoggerConfig
) => {
  const logWithLevel = (
    message: string,
    logContext: DatadogLogContext,
    level: 'info' | 'warn' | 'error' | 'debug'
  ): void => {
    const expandedContext: DatadogLogContext = {
      ...logContext,
      status: level,
    };
    logLevelHandler(message, expandedContext, config);
    logToDatadog(message, expandedContext, config, loggerInstance);
  };

  const log = (message: string, logContext: DatadogLogContext = {}): void => {
    logWithLevel(message, logContext, 'info');
  };

  const info = (message: string, logContext: DatadogLogContext = {}): void => {
    logWithLevel(message, logContext, 'info');
  };

  const warn = (message: string, logContext: DatadogLogContext = {}): void => {
    logWithLevel(message, logContext, 'warn');
  };

  const error = (
    message: string,
    logContext: DatadogLogContext & { error: Error } & Record<string, unknown>
  ): void => {
    const { traceId, ...cleanContext } = logContext;
    const finalMessage = traceId ? prependTraceId(message, traceId) : message;
    logWithLevel(finalMessage, cleanContext, 'error');
  };

  const debug = (message: string, logContext: DatadogLogContext = {}): void => {
    logWithLevel(message, logContext, 'debug');
  };

  return {
    log,
    info,
    warn,
    error,
    debug,
    generateTraceId,
  };
};
